import { AdminContentTypeListSchema, AdminSubscriptionSchema, ClientDomainsSchema, LiveChatStatusSchema, SubscriptionStatusSchema, type ContentListItem, type ListQuery, RoleListSchema, TwoFactorStatusSchema } from '@bak/types'
import { z } from 'zod'
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'

import { api, apiVoid } from './client.js'
import { CONTENT_RESOURCES, type ContentResourceKey, schemas } from './resources.js'

// Query keys: ['admin', area, ...]. Mutations invalidate their area.

export const meQuery = queryOptions({
  queryKey: ['admin', 'me'],
  queryFn: () => api('/auth/me', schemas.me),
  retry: false,
  staleTime: 5 * 60_000,
})

/**
 * What the sign-in page needs (the Turnstile site key), and whether this site signs in with central Digimasi accounts
 * (#228 step 3), which changes how users are added and how passwords are reset.
 */
export const authConfigQuery = queryOptions({
  queryKey: ['auth', 'captcha'],
  queryFn: () => api('/auth/captcha', z.object({ siteKey: z.string().nullable(), central: z.boolean().default(false) })),
  staleTime: Infinity,
  retry: 1,
})

/** The signed-in person's two-factor sign-in (#227): Profile, and the grace-period banner. */
export const twoFactorQuery = queryOptions({
  queryKey: ['admin', 'two-factor'],
  queryFn: () => api('/auth/2fa', TwoFactorStatusSchema),
  staleTime: 5 * 60_000,
  retry: false,
})

export const dashboardQuery = queryOptions({
  queryKey: ['admin', 'dashboard'],
  queryFn: () => api('/dashboard', schemas.dashboard),
  staleTime: 60_000,
})

/**
 * Whether this CMS has the live chat (its API has the chat binding) and how many chats need a person: the menu's badge.
 * Asked only by users who may see chats; every 15 s while the CMS is open.
 */
export const liveChatStatusQuery = queryOptions({
  queryKey: ['admin', 'livechat', 'status'],
  queryFn: () => api('/livechat/status', LiveChatStatusSchema),
  staleTime: 10_000,
  refetchInterval: 15_000,
  retry: false,
})

export const rolesQuery = queryOptions({ queryKey: ['admin', 'roles'], queryFn: () => api('/roles', RoleListSchema), staleTime: 15_000 })

export const contentTypesQuery = queryOptions({
  queryKey: ['admin', 'content-types'],
  queryFn: () => api('/content-types', AdminContentTypeListSchema),
  staleTime: 30_000,
})

/** The subscription stage for the expiry banner (every user). Refreshed every few minutes so a confirmation shows up. */
/** Settings → Domains (kept by Digimasi's console). Re-read every 30 s while a domain is on its way. */
export const domainsQuery = queryOptions({
  queryKey: ['admin', 'domains'],
  queryFn: () => api('/domains', ClientDomainsSchema),
  retry: false,
  refetchInterval: (q) => (q.state.data?.domains.some((d) => d.state === 'waiting' || d.state === 'connecting') ? 30_000 : false),
})

export const subscriptionStatusQuery = queryOptions({
  queryKey: ['admin', 'subscription', 'status'],
  queryFn: () => api('/subscription/status', SubscriptionStatusSchema),
  staleTime: 60_000,
  refetchInterval: 5 * 60_000,
  retry: false,
})

/** The subscription box, renewal details and payment history (Admins). */
export const subscriptionQuery = queryOptions({
  queryKey: ['admin', 'subscription', 'detail'],
  queryFn: () => api('/subscription', AdminSubscriptionSchema),
  staleTime: 0,
})

export const sitePlanQuery = queryOptions({
  queryKey: ['admin', 'site', 'plan'],
  queryFn: () => api('/site/entitlements', schemas.sitePlan),
  // Usage changes with every page or user added, so refetch whenever a screen needs it.
  staleTime: 0,
})

export type ContentListParams = Partial<ListQuery> & { section?: string; categoryId?: string; kind?: string }

export const contentListQuery = (key: ContentResourceKey, params: ContentListParams) =>
  queryOptions({
    queryKey: ['admin', key, 'list', params],
    queryFn: ({ signal }) =>
      api(CONTENT_RESOURCES[key].path, schemas.contentList, { query: params as Record<string, string | number>, signal }),
    placeholderData: keepPreviousData,
  })

export const contentDocQuery = <K extends ContentResourceKey>(key: K, id: string) =>
  queryOptions({
    queryKey: ['admin', key, 'doc', id],
    queryFn: () => api(`${CONTENT_RESOURCES[key].path}/${id}`, CONTENT_RESOURCES[key].schema),
  })

/** Every row of a resource for pickers (categories, products, industries). Capped at 100. */
export const contentOptionsQuery = (key: ContentResourceKey) =>
  queryOptions({
    queryKey: ['admin', key, 'options'],
    queryFn: async (): Promise<ContentListItem[]> =>
      (await api(CONTENT_RESOURCES[key].path, schemas.contentList, { query: { limit: 100 } })).items,
    staleTime: 60_000,
  })

export function useSaveContent<K extends ContentResourceKey>(key: K) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, input }: { id: string | null; input: unknown }) => {
      const path = CONTENT_RESOURCES[key].path
      return id
        ? api(`${path}/${id}`, schemas.created, { method: 'PUT', body: input })
        : api(path, schemas.created, { method: 'POST', body: input })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin'] }),
  })
}

export function useDeleteContent(key: ContentResourceKey) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiVoid(`${CONTENT_RESOURCES[key].path}/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin'] }),
  })
}

export const mediaListQuery = (params: { q?: string; limit: number; offset: number }) =>
  queryOptions({
    queryKey: ['admin', 'media', 'list', params],
    queryFn: ({ signal }) => api('/media', schemas.mediaList, { query: params, signal }),
    placeholderData: keepPreviousData,
  })

export const mediaDocQuery = (id: string) =>
  queryOptions({
    queryKey: ['admin', 'media', 'doc', id],
    queryFn: () => api(`/media/${id}`, schemas.media),
    staleTime: 5 * 60_000,
  })

export function useUploadMedia() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ file, alt }: { file: File | Blob; alt?: string }) => {
      const fd = new FormData()
      fd.set('file', file)
      if (alt) fd.set('alt', alt)
      return api('/media', schemas.media, { method: 'POST', formData: fd })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'media'] }),
  })
}
